import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChartData } from "chart.js";
import { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { getTicks, getTooltip } from "../../utils/chartjsTheming";
import { BAR_FILL_ALPHA } from "../general/dataAtoms/shared";

export type InteractiveBar = {
  label: string;
  value: number;
} & (
  | {
      href: string;
    }
  | { onClick: () => void }
);

/**
 * The height of the chart, in px, where what it stands in gives it none. In a
 * frame with a height of its own the chart fills the frame.
 */
const CHART_HEIGHT = 260;

/** A bar is no wider than this, in px: few bars do not fill the chart. */
const MAX_BAR_THICKNESS = 56;

/** How far the end of a bar is rounded, in px. */
const BAR_RADIUS = 4;

/**
 * Signed amounts as bars from the line of zero, a gain above it and a loss
 * hanging below. Each bar leads somewhere: a click follows its link.
 */
export const LinkedBarChart = ({ data }: { data: InteractiveBar[] }) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const navigate = useNavigate();

  const chartData: ChartData<"bar", number[]> = useMemo(() => {
    const { positive, negative } = theme.palette.data;
    return {
      labels: data.map(({ label }) => label),
      datasets: [
        {
          data: data.map(({ value }) => value),
          backgroundColor: data.map(({ value }) =>
            alpha(value < 0 ? negative.main : positive.main, BAR_FILL_ALPHA),
          ),
          borderColor: data.map(({ value }) =>
            value < 0 ? negative.dark : positive.dark,
          ),
          borderWidth: 1,
          borderRadius: BAR_RADIUS,
        },
      ],
    };
  }, [data, theme]);

  return (
    <Box sx={{ position: "relative", height: "100%", minHeight: CHART_HEIGHT }}>
      <Chart
        type="bar"
        data={chartData}
        options={{
          interaction: { mode: "index", intersect: false },
          // the chart fills the frame it is given rather than keeping a shape
          maintainAspectRatio: false,
          datasets: { bar: { maxBarThickness: MAX_BAR_THICKNESS } },
          onClick: (_e, elements) => {
            if (elements.length === 0) return;
            const bar = data[elements[0].index];
            if ("href" in bar) {
              navigate(bar.href);
            } else {
              bar.onClick();
            }
          },
          onHover: (event, elements) => {
            const target = event.native?.target;
            if (!(target instanceof HTMLElement)) return;
            target.style.cursor = elements.length ? "pointer" : "default";
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...getTooltip(theme),
              mode: "index",
              intersect: false,
              displayColors: true,
              callbacks: {
                title: (items) => items?.[0]?.label ?? "",
                label: (item) => currency.format(item.parsed.y ?? 0),
              },
            },
          },
          scales: {
            x: {
              // a bar is named in its tooltip: the names are too many and too
              // long to stand under the bars
              ticks: { display: false },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: {
                ...getTicks(theme),
                callback: (value) =>
                  typeof value === "number"
                    ? currency.formatCompact(value)
                    : value,
              },
              grid: {
                // the line of zero stands out from the lines of the other amounts
                color: (context) =>
                  context.tick.value === 0
                    ? theme.palette.text.disabled
                    : theme.palette.divider,
              },
            },
          },
        }}
      />
    </Box>
  );
};
